import React, { useEffect, useState, Suspense, lazy } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import axios from 'axios';
import './App.css';
import './responsive.css';
import packageInfo from '../package.json';
import buildInfo from './buildInfo.json'; // généré au build (commit + date réelle) — voir scripts/generate-build-info.js
import SkeletonLoader from './SkeletonLoader';
import { getCacheOrFetch, setCache, getCache, CACHE_KEYS } from './utils/cache';
import { t } from './i18n';

// ⚡ Lazy loading des composants (code splitting)
const CRMCallerInfo = lazy(() => import('./CRMCallerInfo'));
const CallTimeline = lazy(() => import('./CallTimeline'));

// Version de l'application Teams (récupérée automatiquement depuis package.json)
const APP_VERSION = packageInfo.version;

// 🧪 MODE TEST par URL — simule un appel entrant alors qu'aucun appel n'est en cours.
// (Remplace l'ancien repli automatique DEV_PHONE_NUMBER, qui masquait les "pas de numéro"
//  en affichant la fiche d'un client sans rapport.)
//   ?test=1              → appel simulé depuis TEST_DEFAULT_PHONE
//   ?test=33612345678    → appel simulé depuis ce numéro
//   alias acceptés : ?testPhone=…  ?mock=…
const TEST_DEFAULT_PHONE = '33159032312';

const readTestMode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('test') ?? params.get('testPhone') ?? params.get('mock');
    if (raw === null) return { enabled: false, phone: null };
    const value = String(raw).trim();
    const isFlagOnly = value === '' || ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
    return { enabled: true, phone: isFlagOnly ? TEST_DEFAULT_PHONE : value };
  } catch {
    return { enabled: false, phone: null };
  }
};
const TEST_MODE = readTestMode();

// Faux callState pour le mode test : l'app se comporte comme si un appel était en cours.
const buildTestCallState = (phone) => {
  const now = Math.floor(Date.now() / 1000);
  return {
    stage: 'CONNECTED',
    callerId: phone,
    callerType: 'phone',
    callChainId: 'test-mode',
    flowName: 'TEST MODE',
    callCreatedOn: now - 42,
    agentConnectedAt: now - 30,
    CDR: {
      DIRECTION: 'IN',
      timestamp: now - 42,
      CALLER_ID: phone,
      T_CALL_ANSWERED: now - 38,
      AGENT_ID: 'test-mode',
      AGENT_DISPLAYNAME: 'Test mode'
    },
    _testMode: true
  };
};

// 🧪 TEST CRM en dur — exécuté EN TOUT PREMIER (avant Teams init / profil agent) pour valider
// l'endpoint Terrena (CORS, clé Ocp-Apim-Subscription-Key, format de réponse).
const CRM_TEST_BASE = 'https://apim-rct-terrena.azure-api.net/Heedify/GetClientByPhone';
const CRM_TEST_HEADERS = {
  'Ocp-Apim-Subscription-Key': '79b89aba408346538b85184e90715698',
  'Accept': 'application/json'
};
// 📋 Liste de numéros testés au démarrage (ajoute / retire librement).
const CRM_TEST_NUMBERS = [
  '0040248222464',
  '0041217028000',
  '0041244470303',
  '00414484755',
  '0041797569276',
  '00420602623189',
  '004312827183',
  '00440192327900',
  '00441242821100',
  '00441353727200',
  '00441772812647',
  '00441995601355',
  '00442897541603',
  '004548279019',
  '004575782365',
  '004576117676',
  '0046418380100',
  '004642249600',
];

// 🔧 DEV ONLY — Token de fallback utilisé quand l'app tourne hors de Teams (ex: localhost).
// ⚠️ À ne JAMAIS utiliser en production. Le token expire ~1h après émission.
const DEV_FALLBACK_TOKEN = 'eyJ0eXAiOiJKV1QiLCJub25jZSI6Ilk2T0hwYXYyQ0hRYjhUVHRORkJEV3ZkNTA4Q1lNRkl4Z1BlenFSYTZ1aGMiLCJhbGciOiJSUzI1NiIsIng1dCI6IlUxc1g4WUZIUzdaNlZsN1ZITEl6VGVqYnZqMCIsImtpZCI6IlUxc1g4WUZIUzdaNlZsN1ZITEl6VGVqYnZqMCJ9.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.YpKm6Ynizkqv2s4frsYzUAnQ9p48Xh0T8X2twfTd1OKkf7q9Mdmpy5wYAoxwNvdRcbK1_KE8zW_TLuryafQ4HAanKYzJbRLQEGN_odLtDg1N813frE5UOHODLEc0-YpiVF6xt600ozVweyS64TUd_PYmEVv7Qx6BnfGZ--pJx9A9rsKjvyxAZkzsrRxXs5K4gAQRiyW_mk2V5D65MI9qxbLer3WeIzDHkLeSvjCOI-9VnUmR3jzT8gsmktkLlftneWBT6an6ciLxqW7iHAvOPwUENsbY-YItju1HosPZCFPchVL2pD-qGQaYwFxhkTxcsq3SNl9XhIdabA-nEHW4wQ';

function App() {
  const [teamsContext, setTeamsContext] = useState(null);
  const [callerData, setCallerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('default');
  const [locale, setLocale] = useState(navigator.language || 'en');
  const [callStateData, setCallStateData] = useState(null);
  const [activeTab, setActiveTab] = useState('crm');
  const [config, setConfig] = useState(null);
  useEffect(() => {
    // Initialiser le Teams SDK
    const initializeTeams = async () => {
      let context = null;
      let userTenantId = null;
      let userObjectId = null;
      let authToken = null;  // 🔑 JWT token Teams pour authentification API

      // 🧪 TEST CRM forcé — DÉSACTIVÉ (fonction conservée plus bas, mais plus appelée au démarrage).
      // Réactiver en décommentant la ligne ci-dessous si besoin de re-tester l'endpoint CRM.
      // await testCRMDirect();

      try {
        await microsoftTeams.app.initialize();
        // ✅ Signaler à Teams que l'app est chargée (manifest showLoadingIndicator:true l'exige,
        // sinon Teams affiche "There was a problem reaching this app" après quelques secondes).
        // Appelé TÔT, avant les fetch profil/CRM : le reste se charge derrière le SkeletonLoader.
        try { microsoftTeams.app.notifySuccess(); } catch (e) { console.warn('notifySuccess failed:', e); }
        console.log('='.repeat(80));
        console.log('🚀 Teams App Initialized');
        console.log('🏷️ DEPLOYED BUILD:', `v${buildInfo.version}`, '| commit:', buildInfo.commit, '| branch:', buildInfo.branch);
        console.log('📅 Build Time (UTC):', buildInfo.buildTime);
        console.log('⏱️ Loaded At:', new Date().toISOString());
        console.log('📌 App Version (package.json):', APP_VERSION);
        console.log('='.repeat(80));

        // 🔍 Afficher la structure complète du SDK Teams
        console.log('='.repeat(80));
        console.log('📦 TEAMS SDK OBJECT STRUCTURE:');
        console.log('microsoftTeams (full object):', microsoftTeams);
        console.log('\n🔧 Available Namespaces:');
        Object.keys(microsoftTeams).forEach(key => {
          if (typeof microsoftTeams[key] === 'object' && microsoftTeams[key] !== null) {
            console.log(`   📁 microsoftTeams.${key}:`, Object.keys(microsoftTeams[key]));
          }
        });
        console.log('\n📞 Meeting namespace:', microsoftTeams.meeting);
        console.log('   Available methods:', microsoftTeams.meeting ? Object.keys(microsoftTeams.meeting) : 'N/A');
        console.log('\n📞 Call namespace:', microsoftTeams.call);
        console.log('   Available methods:', microsoftTeams.call ? Object.keys(microsoftTeams.call) : 'N/A');
        console.log('='.repeat(80));

        // Obtenir le contexte Teams
        context = await microsoftTeams.app.getContext();
        setTeamsContext(context);
        console.log('Teams Context:', context);

        // 🔍 Analyser le contexte pour détecter le type d'appel
        console.log('='.repeat(80));
        console.log('🔍 CALL CONTEXT ANALYSIS:');
        console.log('   Frame Context:', context.page?.frameContext);
        console.log('   Meeting ID:', context.meeting?.id);
        console.log('   Call Info:', context.call);
        console.log('   Page Sub-page ID:', context.page?.subPageId);
        console.log('   Page ID:', context.page?.id);
        console.log('   Full Page Object:', context.page);

        // 🧪 DIAGNOSTIC TPE — meeting (conférence) vs call (1:1) ?
        // Hypothèse : un appel TPE est rendu comme un MEETING → la calling extension
        // (side panel personnel) n'est pas éligible → l'app Heedify ne s'affiche pas.
        {
          const meetingId = context.meeting?.id || null;
          const callId = context.call?.id || null;
          const frame = context.page?.frameContext || null;
          const chatId = context.chat?.id || null;
          let verdict;
          if (meetingId && !callId) verdict = '🧩 MEETING/conférence → calling extension NON éligible (cause probable du side panel manquant)';
          else if (callId && !meetingId) verdict = '📞 CALL 1:1 → calling extension éligible';
          else if (meetingId && callId) verdict = '❓ meeting.id ET call.id présents → à inspecter';
          else verdict = '❓ ni meeting.id ni call.id → surface inattendue (frameContext: ' + frame + ')';
          console.log('='.repeat(80));
          console.log('🧪 DIAGNOSTIC TPE — surface de l\'appel');
          console.table({
            frameContext: frame,
            'meeting.id': meetingId || '—',
            'call.id': callId || '—',
            'chat.id': chatId || '—',
            'meeting.conversationId': context.meeting?.conversationId || '—',
          });
          console.log('   👉 VERDICT:', verdict);
          console.log('='.repeat(80));
        }

        // Vérifier si c'est un appel PSTN
        const isPSTNCall = (
          context.page?.frameContext === 'sidePanel' ||
          context.page?.frameContext === 'meetingSidePanel'
        ) && context.meeting;
        console.log('   🎯 Detected PSTN Call:', isPSTNCall);
        console.log('='.repeat(80));

        // 🔑 Récupérer le JWT token d'authentification Teams
        try {
          authToken = await microsoftTeams.authentication.getAuthToken();
          console.log('='.repeat(80));
          console.log('🔐 TEAMS JWT TOKEN (complet):');
          console.log(authToken);
          console.log('='.repeat(80));
          console.log('📋 Longueur du token:', authToken.length, 'caractères');

          // Décoder le token pour voir son contenu (partie payload)
          try {
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            console.log('📦 JWT Token Payload (décodé):');
            console.log(payload);
            console.log('   Tenant ID (tid):', payload.tid);
            console.log('   User ID (oid):', payload.oid);
            console.log('   UPN:', payload.upn);
            console.log('   Expiration:', new Date(payload.exp * 1000).toLocaleString());

            // ✅ UTILISER tid et oid du JWT pour l'API
            userTenantId = payload.tid;
            userObjectId = payload.oid;
            console.log('✅ Extracted from JWT for API calls:');
            console.log('   → Tenant ID (tid):', userTenantId);
            console.log('   → User ID (oid):', userObjectId);
          } catch (decodeError) {
            console.warn('⚠️ Could not decode token payload:', decodeError);
          }
          console.log('='.repeat(80));
        } catch (tokenError) {
          console.error('❌ Failed to get auth token:', tokenError);
          console.log('   This is normal if running outside Teams or if auth is not configured');
        }

        // 🎯 VÉRIFIER LA DISPONIBILITÉ DE L'API CALL
        if (microsoftTeams.call) {
          console.log('='.repeat(80));
          console.log('📞 CALL API AVAILABLE');
          console.log('   Available methods:', Object.keys(microsoftTeams.call));
          console.log('='.repeat(80));
        } else {
          console.log('⚠️ microsoftTeams.call API not available in this context');
        }

        // Détecter et appliquer le thème Teams
        const currentTheme = context.app?.theme || 'default';
        setTheme(currentTheme);
        applyTeamsTheme(currentTheme);

        // Détecter la langue du client Teams (ex: "fr-fr", "en-us") pour les messages traduits
        const currentLocale = context.app?.locale || navigator.language || 'en';
        setLocale(currentLocale);
        console.log('🌍 Teams locale:', currentLocale);

        // Écouter les changements de thème
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          console.log('Theme changed to:', newTheme);
          setTheme(newTheme);
          applyTeamsTheme(newTheme);
        });

      } catch (error) {
        console.error('Error initializing Teams SDK:', error);
        console.log('⚠️ Running outside Teams context - using fallback configuration');
        setError(error.message);
      }

      // 🔧 Fallback DEV: si on n'est pas dans Teams, utiliser le token de dev
      if (!authToken && DEV_FALLBACK_TOKEN) {
        console.warn('🔧 Not in Teams - using DEV_FALLBACK_TOKEN');
        authToken = DEV_FALLBACK_TOKEN;
        try {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          userTenantId = payload.tid;
          userObjectId = payload.oid;
          console.log('   Decoded dev token → tid:', userTenantId, '| oid:', userObjectId, '| upn:', payload.upn);
          const expDate = new Date(payload.exp * 1000);
          if (expDate < new Date()) {
            console.warn('⚠️ DEV token expired at', expDate.toLocaleString());
          }
        } catch (e) {
          console.error('❌ Could not decode DEV_FALLBACK_TOKEN:', e);
        }
      }

      // ÉTAPE 0 (parallèle): on LANCE sans attendre les requêtes qui ne dépendent PAS du call id.
      //  - agentProfile       : profil agent (callContext → CRM)
      //  - agentConnectedCall : appel EN COURS de l'agent (fallback callState, agentId = oid du token)
      // Elles tournent pendant le loadConfiguration + la récup meetingDetails. getCallState (par id),
      // lui, sera lancé à l'ÉTAPE 2.5 dès que le callId est connu.
      console.log('🔄 ÉTAPE 0: Lancement parallèle (agentProfile + agentConnectedCall)...');
      const agentProfilePromise = fetchAgentProfile(userTenantId, userObjectId, authToken);
      const agentConnectedCallPromise = (userTenantId && authToken)
        ? fetchAgentConnectedCall(userTenantId, authToken)
        : Promise.resolve(null);

      // ÉTAPE 1: Charger la configuration (URLs) depuis Heedify
      // ⚠️ On utilise les valeurs du JWT (tid, oid) et le token pour authentification
      console.log('🔄 ÉTAPE 1: Loading Heedify configuration...');
      const configuration = await loadConfiguration(userTenantId, userObjectId, context, authToken);
      setConfig(configuration);
      console.log('✅ Configuration loaded:', configuration);

      // ÉTAPE 2: Obtenir les détails de l'appel/meeting et extraire le numéro
      let phoneNumber = null;
      let meetingDetails = null; // hissé hors du if pour réutiliser details.callId à l'ÉTAPE 2.5
      if (context) {
        let callSuccess = false;

        // 🧪 DIAG CORRÉLATION — tout ce que Teams expose pour relier l'appel au callState backend.
        // Le callState (CallsStates) est indexé par GUID Heedify (ex: 03003a80-…), PAS par le meeting.id Teams.
        // Aucun champ du callState ne contient le threadId Teams → le seul pont = l'oid agent : user.id === CDR.AGENT_ID.
        console.log('='.repeat(80));
        console.log('🧪 DIAG CORRÉLATION (Teams → backend callState)');
        console.table({
          'user.id (agent oid)': context?.user?.id || '—',
          'userPrincipalName': context?.user?.userPrincipalName || '—',
          'meeting.id (Teams)': context?.meeting?.id || '—',
          'chat.id (threadId)': context?.chat?.id || '—',
          'call.id': context?.call?.id || '—',
        });
        console.log('   👉 Corréler le callState par AGENT_ID = user.id :', context?.user?.id || '❌');
        console.log('   (le meeting.id Teams ne sert PAS de clé GetCallState)');
        console.log('='.repeat(80));

        // Méthode 1: Essayer getMeetingDetailsVerbose (pour meetings normaux)
        try {
          console.log('🔄 Trying getMeetingDetailsVerbose()...');
          meetingDetails = await microsoftTeams.meeting.getMeetingDetailsVerbose();
          console.log('='.repeat(80));
          console.log('📞 MEETING DETAILS (Structure complète):');
          console.log(JSON.stringify(meetingDetails, null, 2));
          console.log('='.repeat(80));
          console.log('Meeting Details (object):', meetingDetails);
          callSuccess = true;
        } catch (meetingError) {
          console.warn('⚠️ getMeetingDetailsVerbose() failed:', meetingError);
          console.log('Error code:', meetingError.errorCode, 'Message:', meetingError.message);

          // Si erreur 1000, essayer d'autres méthodes pour PSTN
          if (meetingError.errorCode === 1000 || meetingError.message?.includes('1000')) {
            console.log('🔄 Error 1000 detected - trying alternative methods for PSTN calls...');

            // Méthode 2: Essayer getMeetingDetails (sans Verbose)
            try {
              console.log('🔄 Trying getMeetingDetails() (non-verbose)...');

              // getMeetingDetails peut utiliser l'ancienne API avec callback
              meetingDetails = await new Promise((resolve, reject) => {
                try {
                  // Essayer avec Promise d'abord
                  const result = microsoftTeams.meeting.getMeetingDetails();
                  if (result && typeof result.then === 'function') {
                    result.then(resolve).catch(reject);
                  } else {
                    resolve(result);
                  }
                } catch (err) {
                  reject(err);
                }
              });

              console.log('✅ getMeetingDetails() succeeded:', meetingDetails);
              callSuccess = true;
            } catch (error2) {
              console.warn('⚠️ getMeetingDetails() also failed:', error2);
            }

            // Méthode 3: Essayer call API et conversation pour PSTN context
            if (!callSuccess) {
              try {
                console.log('🔄 Trying call API and conversation for PSTN context...');

                // Vérifier les namespaces disponibles
                console.log('   📊 Available call API methods:', microsoftTeams.call ? Object.keys(microsoftTeams.call) : 'N/A');
                console.log('   📊 Context.call contents:', context.call);
                console.log('   📊 Context.meeting contents:', context.meeting);

                // Vérifier chat API
                if (microsoftTeams.chat) {
                  console.log('   📊 Chat API available:', Object.keys(microsoftTeams.chat));
                }

                // Vérifier people API
                if (microsoftTeams.people) {
                  console.log('   📊 People API available:', Object.keys(microsoftTeams.people));
                }

                // Vérifier conversation API
                if (microsoftTeams.conversations) {
                  console.log('   📊 Conversations API available:', Object.keys(microsoftTeams.conversations));
                }

                // Explorer les propriétés du page context
                console.log('   📄 Page context details:');
                console.log('      - frameContext:', context.page?.frameContext);
                console.log('      - id:', context.page?.id);
                console.log('      - subPageId:', context.page?.subPageId);

                // Chercher dans chat/thread context
                console.log('   💬 Chat/Thread context:');
                console.log('      - chat:', context.chat);
                console.log('      - channel:', context.channel);
                console.log('      - team:', context.team);

                // Explorer toutes les propriétés du contexte qui pourraient contenir le numéro
                console.log('   🔍 Searching for phone number in context...');
                console.log('   - context.call:', context.call);
                console.log('   - context.meeting:', context.meeting);
                console.log('   - context.page:', context.page);
                console.log('   - context.user:', context.user);

                // Créer un objet meetingDetails minimal à partir du contexte
                meetingDetails = {
                  details: {
                    type: 'pstn',
                    callerId: context.call?.callerId || null,
                    pstnParticipants: context.call?.participants || []
                  },
                  organizer: {
                    displayName: context.call?.callerId || 'Unknown',
                    phoneNumber: context.call?.callerId || null
                  },
                  _source: 'call-api-fallback',
                  _rawContext: {
                    call: context.call,
                    meeting: context.meeting
                  }
                };

                console.log('✅ Created PSTN call context from call API:', meetingDetails);
                console.log('   📱 Extracted phone number:', meetingDetails.organizer.phoneNumber);
                callSuccess = true;
              } catch (error3) {
                console.warn('⚠️ Call API also failed:', error3);
              }
            }

            // Méthode 4: Utiliser le contexte pour extraire les infos disponibles
            if (!callSuccess) {
              console.log('🔄 Using Teams context as last resort...');
              console.log('   📊 Full context dump:', JSON.stringify(context, null, 2));

              // Fonction pour chercher récursivement un numéro de téléphone
              const findPhoneNumber = (obj, path = '') => {
                if (!obj) return null;

                // Patterns de numéro de téléphone
                const phoneRegex = /^\+?[\d\s\-()]{8,}$/;

                // Vérifier si c'est une string qui ressemble à un numéro
                if (typeof obj === 'string' && phoneRegex.test(obj)) {
                  console.log(`   🎯 Found potential phone number at ${path}:`, obj);
                  return obj;
                }

                // Chercher dans les propriétés communes
                if (typeof obj === 'object') {
                  const phoneKeys = ['phoneNumber', 'phone', 'callerId', 'pstnNumber', 'number', 'callingNumber'];
                  for (const key of phoneKeys) {
                    if (obj[key]) {
                      console.log(`   🎯 Found phone in ${path}.${key}:`, obj[key]);
                      return obj[key];
                    }
                  }

                  // Chercher récursivement (limité à 2 niveaux)
                  if (path.split('.').length < 3) {
                    for (const [key, value] of Object.entries(obj)) {
                      const found = findPhoneNumber(value, path ? `${path}.${key}` : key);
                      if (found) return found;
                    }
                  }
                }

                return null;
              };

              const foundPhone = findPhoneNumber(context, 'context');

              // Vérifier si c'est un contexte d'appel (meeting)
              const isCallContext = (
                context.meeting ||
                context.page?.frameContext === 'sidePanel' ||
                context.page?.frameContext === 'meetingSidePanel'
              );

              if (isCallContext) {
                meetingDetails = {
                  details: {
                    type: 'pstn-from-context',
                    scheduledStartTime: context.meeting?.startTime || null,
                    scheduledEndTime: context.meeting?.endTime || null,
                    joinUrl: context.meeting?.joinUrl || null,
                    callerId: foundPhone,
                    frameContext: context.page?.frameContext
                  },
                  organizer: {
                    id: context.user?.id,
                    displayName: context.user?.displayName || 'Unknown',
                    phoneNumber: foundPhone
                  },
                  _source: 'context-fallback',
                  _note: 'Limited info - PSTN call detected but API unavailable',
                  _fullContext: context
                };

                console.log('✅ Created minimal meeting context:', meetingDetails);
                console.log('   📱 Phone number found:', foundPhone || '❌ NOT FOUND');
                console.log('   🖼️ Frame context:', context.page?.frameContext);
                callSuccess = true;
              } else {
                console.log('❌ Not in a call context - no meeting or sidePanel detected');
              }
            }
          }
        }

        if (callSuccess && meetingDetails) {
          phoneNumber = extractPhoneNumber(meetingDetails);
          console.log('📱 Extracted phone from meeting:', phoneNumber || 'NOT FOUND');
        }
      }

      // ÉTAPE 2.5: Récupérer le callState via le call id (context.meeting.id), puis le callerId.
      // Le callerId du callState est prioritaire sur le numéro extrait de Teams pour l'appel CRM.
      let callState = null;
      let phoneSource = phoneNumber ? 'teams-extraction' : null;
      // Le call id pour GetCallState vient de meetingDetails.details.callId (getMeetingDetailsVerbose),
      // avec fallback sur context.meeting.id.
      const callId = meetingDetails?.details?.callId || context?.meeting?.id;
      console.log('🔎 ÉTAPE 2.5 inputs:', {
        callId: callId || '❌ none',
        detailsCallId: meetingDetails?.details?.callId || '❌ none',
        meetingIdFromContext: context?.meeting?.id || '❌ none',
        conversationId: meetingDetails?.conversation?.id || '❌ none',
        callType: meetingDetails?.details?.type || '❌ none',
        hasTenant: !!userTenantId,
        hasToken: !!authToken,
        phoneFromTeams: phoneNumber || '❌ none'
      });
      // ÉTAPE 2.5: GetCallState par call id — lancé MAINTENANT (le callId vient d'être calculé),
      // en // de l'agentConnectedCall de l'ÉTAPE 0. PRIORITAIRE s'il renvoie non nul.
      const canGetById = !!(callId && userTenantId && authToken);
      if (!canGetById) {
        console.log('ℹ️ GetCallState skipped (need meeting.id + tenant + token):', {
          callId: !!callId, tenant: !!userTenantId, token: !!authToken
        });
      } else {
        console.log('🔄 ÉTAPE 2.5: Fetching callState via GetCallState...', { tenant: userTenantId, id: callId });
      }
      const callStateByIdPromise = canGetById
        ? fetchCallState(userTenantId, callId, authToken)
        : Promise.resolve(null);

      // Timeline réactive : on stocke DÈS qu'une source arrive. GetCallState (prioritaire) écrase
      // le fallback ; le fallback n'est posé que si le prioritaire n'a pas (encore) renvoyé non nul.
      // (primaryCallState : undefined = pas résolu, null = résolu vide, objet = résolu non nul)
      let primaryCallState;
      agentConnectedCallPromise.then(cs => {
        if (cs && !primaryCallState) {
          console.log('✅ Fallback (agent connecté) reçu → timeline');
          setCallStateData(cs);
        }
      });
      callStateByIdPromise.then(cs => {
        primaryCallState = cs;
        if (cs) {
          console.log('✅ GetCallState reçu (prioritaire) → timeline');
          setCallStateData(cs);
        }
      });

      // On a besoin du callState résolu pour le numéro CRM → on attend les 2 sources (déjà en vol).
      const resolvedById = await callStateByIdPromise;
      const resolvedAgentCall = await agentConnectedCallPromise;
      callState = resolvedById || resolvedAgentCall; // GetCallState prioritaire, sinon fallback

      if (callState) {
        console.log('📦 CallState summary:', {
          stage: callState.stage,
          callerId: callState.callerId,
          callerType: callState.callerType,
          callChainId: callState.callChainId,
          flowName: callState.flowName,
          direction: callState.CDR?.DIRECTION,
          source: resolvedById ? 'GetCallState' : 'GetAgentConnectedCall'
        });
      } else {
        console.warn('⚠️ Aucun callState (ni par id, ni par agent connecté)');
      }

      if (callState?.callerId) {
        // ⚠️ Ne pas utiliser le callerId si c'est un ID Teams (callerType="user"), car le CRM
        // attend un numéro de téléphone. Les appels PSTN (callerType="phone") et OBO (phone) sont OK.
        if (callState.callerType === "user") {
          console.warn('⚠️ callState.callerId is a Teams user ID, not a phone number - cannot query CRM:', callState.callerId);
          console.warn('   → Skipping CRM query (CRM expects phone number, not Teams ID)');
          // On ne met pas à jour phoneNumber, donc le CRM ne sera pas interrogé
        } else {
          console.log('✅ callState.callerId (→ CRM, prioritaire):', callState.callerId, '| callerType:', callState.callerType);
          phoneNumber = callState.callerId; // priorité au callerId du callState
          phoneSource = `callState.callerId (${callState.callerType || 'unknown'})`;
        }
      } else if (callState) {
        console.warn('⚠️ No callerId in callState - keeping phone extracted from Teams:', phoneNumber || '❌ none');
      }

      // 🧪 MODE TEST (?test=…) : prioritaire sur tout le reste. On force le numéro et, s'il n'y a
      // pas de vrai appel, on fabrique un callState pour que la timeline s'affiche quand même.
      if (TEST_MODE.enabled) {
        console.warn('='.repeat(80));
        console.warn('🧪 MODE TEST ACTIF (paramètre URL) — appel simulé');
        console.warn('   Numéro utilisé:', TEST_MODE.phone);
        console.warn('   Appel Teams réel détecté ?', !!callState);
        console.warn('='.repeat(80));
        phoneNumber = TEST_MODE.phone;
        phoneSource = 'URL ?test';
        if (!callState) {
          callState = buildTestCallState(phoneNumber);
          setCallStateData(callState);
        }
      }

      // Aucun repli automatique : on préfère "no client data" à la fiche d'un client sans rapport.
      // Pour tester hors appel réel, utiliser ?test=1 ou ?test=<numéro>.
      if (!phoneNumber) {
        console.warn('⚠️ Aucun numéro résolu (callState KO ou appel interne Teams) → pas d\'appel CRM');
        console.warn('   💡 Pour simuler un appel : ajouter ?test=1 ou ?test=<numéro> à l\'URL');
      }

      // agentProfile a été lancé en // à l'ÉTAPE 0 : on l'attend ici, juste avant le CRM qui en a besoin.
      const agentProfile = await agentProfilePromise;

      // 🧭 Résumé du pipeline juste avant l'appel CRM (facile à repérer dans les logs)
      console.log('='.repeat(80));
      console.log('🧭 PIPELINE SUMMARY (avant CRM):');
      console.log('   → Identifiant envoyé au CRM:', phoneNumber || '❌ none');
      console.log('   → Source de l\'identifiant:', phoneSource || 'none');
      console.log('   → agentProfile présent:', !!agentProfile);
      console.log('   → callContext.url:', agentProfile?.callContext?.url || '❌ none');
      console.log('   → callContext.method:', agentProfile?.callContext?.method || '(GET par défaut)');
      console.log('='.repeat(80));

      // ÉTAPE 3: Appeler le CRM via callContext du profil agent (numéro réel de l'appel)
      if (phoneNumber && agentProfile?.callContext?.url) {
        console.log('🔄 ÉTAPE 3: Calling CRM via agentProfile.callContext...');
        const crmData = await loadCRMFromCallContext(agentProfile.callContext, phoneNumber);
        if (crmData) {
          setCallerData(crmData);
          setIsLoading(false);
        } else {
          console.warn('⚠️ CRM callContext request failed - showing "no client data" message');
          showNoClientData();
        }
      } else {
        if (!phoneNumber) console.warn('⚠️ No phone available - showing "no client data" message');
        if (!agentProfile?.callContext?.url) console.warn('⚠️ No callContext in agent profile - showing "no client data" message');
        showNoClientData();
      }
    };
    initializeTeams();
  }, []);
  const applyTeamsTheme = (themeName) => {
    // Supprimer les anciennes classes de thème
    document.body.classList.remove('theme-default', 'theme-dark', 'theme-contrast');
    // Ajouter la nouvelle classe de thème
    document.body.classList.add(`theme-${themeName}`);
  };
  // 🧪 Test CRM autonome (clé en dur) sur une LISTE de numéros. Conservé mais NON appelé (voir + haut).
  // eslint-disable-next-line no-unused-vars
  const testCRMDirect = async () => {
    console.log('='.repeat(80));
    console.log(`🧪 TEST CRM (forcé, AVANT tout) — ${CRM_TEST_NUMBERS.length} numéros en parallèle`);
    const results = await Promise.all(
      CRM_TEST_NUMBERS.map(async (numero) => {
        try {
          const { data } = await axios.get(`${CRM_TEST_BASE}/${numero}`, { headers: CRM_TEST_HEADERS, timeout: 15000 });
          return {
            numero,
            http: 200,
            client: data?.nom || '',
            societe: data?.societe || '',
            codeClient: data?.codeClient || '',
          };
        } catch (error) {
          return {
            numero,
            http: error.response?.status ?? 'ERR',
            client: '',
            societe: '',
            codeClient: typeof error.response?.data === 'string' ? error.response.data : (error.message || ''),
          };
        }
      })
    );
    const ok = results.filter((r) => r.http === 200).length;
    console.table(results);
    console.log(`🧪 TEST CRM — ${ok}/${results.length} numéros trouvés (HTTP 200)`);
    console.log('='.repeat(80));
    return results;
  };

  const fetchAgentProfile = async (tenantId, userId, token) => {
    if (!tenantId || !userId || !token) {
      console.warn('⚠️ fetchAgentProfile: missing tenantId, userId or token', { tenantId, userId, hasToken: !!token });
      return null;
    }

    // ⚠️ Endpoint V2 sur provisioning.heedify.io (CORS OK). L'ancien /MyAgentProfile/ (sans /api/V2)
    // renvoyait 400 + aucun header CORS. Token en Bearer, comme loadConfiguration.
    const url = `https://provisioning.heedify.io/api/V2/MyAgentProfile/${tenantId}/${userId}`;
    console.log('='.repeat(80));
    console.log('🌐 Agent Profile request:');
    console.log('   Endpoint:', url);
    console.log('   Tenant ID:', tenantId);
    console.log('   User ID:', userId);
    console.log('='.repeat(80));

    try {
      const response = await axios.get(url, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('='.repeat(80));
      console.log('✅ AGENT PROFILE RESPONSE:');
      console.log(response.data);
      console.log('   → callContext présent ?', !!response.data?.callContext, '| url:', response.data?.callContext?.url || '❌ none');
      console.log('='.repeat(80));
      return response.data;
    } catch (error) {
      console.error('❌ fetchAgentProfile failed:', error.response?.status, error.response?.data || error.message);
      return null;
    }
  };

  // Récupère le callState Heedify à partir du call id (context.meeting.id).
  // Le token Teams déjà obtenu est passé en header Authorization (accepté par GetCallState).
  const fetchCallState = async (tenantId, callId, token) => {
    if (!tenantId || !callId || !token) {
      console.warn('⚠️ fetchCallState: missing tenantId, callId or token', { tenantId, callId, hasToken: !!token });
      return null;
    }

    const url = `https://func-provisioning-prod-ne-02.azurewebsites.net/api/V2/GetCallState/${tenantId}/${callId}`;
    console.log('='.repeat(80));
    console.log('🌐 CallState request:');
    console.log('   Endpoint:', url);
    console.log('   Tenant ID:', tenantId);
    console.log('   Call ID:', callId);
    console.log('='.repeat(80));

    try {
      const response = await axios.get(url, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('✅ CALLSTATE RESPONSE:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ fetchCallState failed:', error.response?.status, error.response?.data || error.message);
      return null;
    }
  };

  // Fallback de fetchCallState : récupère l'appel EN COURS de l'agent (stage AGENT_CONNECTED)
  // sans call id. L'agentId est déduit côté serveur depuis le token (oid). Sert aux appels
  // FLOW/TPE où GetCallState ne matche pas (le doc n'a pas de serverCallId).
  const fetchAgentConnectedCall = async (tenantId, token) => {
    if (!tenantId || !token) {
      console.warn('⚠️ fetchAgentConnectedCall: missing tenantId or token', { tenantId, hasToken: !!token });
      return null;
    }

    const url = `https://func-provisioning-prod-ne-02.azurewebsites.net/api/V2/GetAgentConnectedCall/${tenantId}`;
    console.log('='.repeat(80));
    console.log('🌐 AgentConnectedCall request (fallback):');
    console.log('   Endpoint:', url);
    console.log('   Tenant ID:', tenantId);
    console.log('='.repeat(80));

    try {
      const response = await axios.get(url, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('✅ AGENT CONNECTED CALL RESPONSE:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ fetchAgentConnectedCall failed:', error.response?.status, error.response?.data || error.message);
      return null;
    }
  };

  const substitutePlaceholders = (input, replacements) => {
    if (typeof input === 'string') {
      let result = input;
      for (const [key, value] of Object.entries(replacements)) {
        const safe = value == null ? '' : String(value);
        result = result.split(`{${key}}`).join(safe);
      }
      return result;
    }
    if (Array.isArray(input)) {
      return input.map(item => substitutePlaceholders(item, replacements));
    }
    if (input && typeof input === 'object') {
      const result = {};
      for (const [k, v] of Object.entries(input)) {
        result[k] = substitutePlaceholders(v, replacements);
      }
      return result;
    }
    return input;
  };

  const extractPhoneNumber = (meetingDetails) => {
    let phone =
      meetingDetails?.details?.originalCallerInfo?.originalCallerInfo?.phoneNumber ||
      meetingDetails?.details?.originalCallerInfo?.phoneNumber ||
      meetingDetails?.originalCallerInfo?.originalCallerInfo?.phoneNumber ||
      meetingDetails?.details?.callerId ||
      meetingDetails?.details?.pstnParticipants?.[0]?.phoneNumber ||
      meetingDetails?.organizer?.phoneNumber ||
      null;

    if (!phone && meetingDetails?.organizer?.displayName?.match(/^\+?[\d\s\-()]+$/)) {
      phone = meetingDetails.organizer.displayName;
    }
    return phone ? phone.replace(/[\s\-()]/g, '') : null;
  };

  /**
   * Le CRM Terrena stocke les numéros sans format canonique : certains fiches sont en
   * "33159032312" (indicatif sans préfixe), d'autres en "0041217028000" (préfixe 00).
   * Le callerId Teams/Heedify arrive lui en E.164 ("+33159032312") → 404 tel quel.
   * On génère donc les variantes plausibles, essayées dans l'ordre jusqu'au premier succès.
   */
  const buildPhoneVariants = (phoneNumber) => {
    const raw = String(phoneNumber || '').replace(/[\s\-().]/g, '');
    if (!raw) return [];
    const digits = raw.replace(/^\+/, '').replace(/^00/, ''); // indicatif nu : 33159032312
    return [...new Set([digits, `00${digits}`, raw])].filter(Boolean);
  };

  const loadCRMFromCallContext = async (callContext, phoneNumber) => {
    if (!callContext?.url) {
      console.warn('⚠️ loadCRMFromCallContext: missing url in callContext');
      return null;
    }

    const method = (callContext.method || 'GET').toUpperCase();
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);
    const variants = buildPhoneVariants(phoneNumber);

    if (!variants.length) {
      console.warn('⚠️ loadCRMFromCallContext: no usable phone number', phoneNumber);
      return null;
    }

    console.log('='.repeat(80));
    console.log('🌐 CRM callContext request:');
    console.log('   Method:', method);
    console.log('   Phone reçu:', phoneNumber);
    console.log('   Variantes essayées:', variants.join(' → '));
    console.log('='.repeat(80));

    let lastError = null;
    for (const variant of variants) {
      // Variantes de placeholder tolérées : {phoneNumber}, {PhoneNumber}, {phone}, {callerId}
      const replacements = {
        phoneNumber: variant,
        PhoneNumber: variant,
        phone: variant,
        callerId: variant
      };
      const url = substitutePlaceholders(callContext.url, replacements);
      const headers = substitutePlaceholders(callContext.headers || {}, replacements);
      const body = substitutePlaceholders(callContext.body || {}, replacements);

      console.log(`🔎 CRM try "${variant}" →`, url);
      if (hasBody) console.log('   Body:', body);

      try {
        const response = await axios({
          url,
          method,
          headers,
          data: hasBody ? body : undefined,
          timeout: 15000
        });
        console.log(`✅ CRM response (format "${variant}"):`, response.data);
        return response.data;
      } catch (error) {
        lastError = error;
        console.warn(`   ↳ échec (${error.response?.status || error.message}) :`, error.response?.data || '');
      }
    }

    console.error('❌ CRM callContext request failed pour toutes les variantes:',
      lastError?.response?.status, lastError?.response?.data || lastError?.message);
    return null;
  };

  /**
   * Charge la configuration depuis l'URL Heedify Provisioning
   * Utilise tid et oid extraits du JWT token en priorité
   *
   * @param {string} tenantId - Tenant ID du JWT (tid)
   * @param {string} userId - User Object ID du JWT (oid)
   * @param {object} context - Contexte Teams (fallback)
   * @param {string} authToken - JWT token Teams pour authentification
   */
  const loadConfiguration = async (tenantId, userId, context, authToken) => {
    try {
      // Utiliser les valeurs du JWT en priorité, sinon fallback sur le contexte
      const finalTenantId = tenantId || context?.user?.tenant?.id || context?.tid
      const finalUserId = userId || context?.user?.id || context?.userObjectId

      // 🎯 Clé de cache unique par utilisateur
      const cacheKey = `${CACHE_KEYS.AGENT_PROFILE}_${finalTenantId}_${finalUserId}`;

      // ⚡ Essayer le cache d'abord (stale-while-revalidate)
      const fetchConfig = async () => {
        // 🎯 CONSTRUIRE L'URL AVEC tid et oid du JWT (Azure Function Provisioning)
        const configUrl = `https://func-provisioning-prod-ne-02.azurewebsites.net/api/V2/MyAgentProfile/${finalTenantId}/${finalUserId}`;

        console.log('🌐 Fetching Heedify Agent Profile Configuration (Azure Function):');
        console.log('   URL:', configUrl);
        console.log('   📊 Parameters:');
        console.log('      → Tenant ID (tid):', finalTenantId, tenantId ? '✅ from JWT' : '⚠️ from fallback');
        console.log('      → User ID (oid):', finalUserId, userId ? '✅ from JWT' : '⚠️ from fallback');

        // 🔌 APPELER L'API AVEC LA CONFIGURATION DU PORTAIL
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };

        // ✅ Ajouter le JWT token Teams dans les headers si disponible
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
          console.log('   🔑 Authorization header added with Teams JWT token');
        } else {
          console.warn('   ⚠️ No authToken available - API call may fail');
        }

        const response = await axios.get(configUrl, {
          timeout: 10000,
          headers: headers
        });

        console.log('✅ Heedify Agent Profile Configuration loaded!');
        console.log('   Response:', response.data);

        // Retourner la configuration complète avec les métadonnées
        return {
          ...response.data,
          _metadata: {
            tenantId,
            userId,
            loadedAt: new Date().toISOString(),
            source: 'heedify-provisioning'
          }
        };
      };

      // ⚡ Utiliser le cache avec TTL de 10 minutes
      return await getCacheOrFetch(cacheKey, fetchConfig, 10 * 60 * 1000);

    } catch (error) {
      console.error('❌ Error loading Heedify configuration:', error);
      console.error('   Error details:', error.response?.data || error.message);

      // Afficher plus de détails sur l'erreur
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Headers:', error.response.headers);
      } else if (error.request) {
        console.error('   No response received from server');
        console.error('   This might be a CORS or network issue');
      }

      // Configuration par défaut en cas d'erreur
      return {
        error: error.message,
        useMockData: true,
        _metadata: {
          loadedAt: new Date().toISOString(),
          source: 'fallback',
          errorMessage: error.message
        }
      };
    }
  };

  /**
   * Charge les données CRM depuis les URLs configurées
   * Utilise le contextURL depuis agentProfile de la configuration Heedify
   */
  const loadCRMData = async (meetingDetails, configuration) => {
    // Si la configuration indique d'utiliser les données mock
    if (configuration?.useMockData) {
      console.log('Using mock data (configuration set to useMockData)');
      showNoClientData();
      return;
    }

    // Vérifier si contextURL existe dans agentProfile
    const contextURL = configuration?.agentProfile?.contextURL;

    if (!contextURL || !contextURL.url) {
      console.warn('⚠️ No contextURL found in agentProfile - using demo data');
      console.log('Configuration received:', configuration);
      showNoClientData();
      return;
    }

    try {
      // 📞 Extraire le numéro PSTN de l'appelant depuis les métadonnées du meeting
      let phoneNumber = null;

      console.log('🔍 Searching for PSTN phone number in meeting details...');
      console.log('   Meeting details source:', meetingDetails?._source || 'getMeetingDetailsVerbose');

      // 🎯 PRIORITÉ 0 (DÉCOUVERTE): originalCallerInfo.originalCallerInfo.phoneNumber (pour PSTN)
      if (!phoneNumber && meetingDetails?.details?.originalCallerInfo?.originalCallerInfo?.phoneNumber) {
        phoneNumber = meetingDetails.details.originalCallerInfo.originalCallerInfo.phoneNumber;
        console.log('   ✅ Found PSTN phone in details.originalCallerInfo.originalCallerInfo.phoneNumber:', phoneNumber);
      }

      // 🎯 PRIORITÉ 1: Details.originalCallerInfo.phoneNumber (variante)
      if (!phoneNumber && meetingDetails?.details?.originalCallerInfo?.phoneNumber) {
        phoneNumber = meetingDetails.details.originalCallerInfo.phoneNumber;
        console.log('   ✅ Found phone in details.originalCallerInfo.phoneNumber:', phoneNumber);
      }

      // 🎯 PRIORITÉ 2: originalCallerInfo au niveau racine
      if (!phoneNumber && meetingDetails?.originalCallerInfo?.originalCallerInfo?.phoneNumber) {
        phoneNumber = meetingDetails.originalCallerInfo.originalCallerInfo.phoneNumber;
        console.log('   ✅ Found phone in originalCallerInfo.originalCallerInfo.phoneNumber:', phoneNumber);
      }

      // 🎯 PRIORITÉ 3: Si c'est un fallback depuis callerId dans details
      if (!phoneNumber && meetingDetails?.details?.callerId) {
        phoneNumber = meetingDetails.details.callerId;
        console.log('   ✅ Found phone in details.callerId:', phoneNumber);
      }

      // 🎯 PRIORITÉ 4: Vérifier les participants PSTN dans details.pstnParticipants
      if (!phoneNumber && meetingDetails?.details?.pstnParticipants && meetingDetails.details.pstnParticipants.length > 0) {
        // Prendre le premier participant PSTN (l'appelant)
        const pstnParticipant = meetingDetails.details.pstnParticipants[0];
        phoneNumber = pstnParticipant.phoneNumber || pstnParticipant.phone || pstnParticipant.number;
        console.log('   ✅ Found PSTN number in details.pstnParticipants:', phoneNumber);
      }

      // 🎯 PRIORITÉ 5: Chercher dans l'organisateur
      if (!phoneNumber && meetingDetails?.organizer) {
        phoneNumber = meetingDetails.organizer.phoneNumber ||
          meetingDetails.organizer.pstnNumber ||
          meetingDetails.organizer.phone;
        if (phoneNumber) {
          console.log('   ✅ Found phone in organizer:', phoneNumber);
        }
      }

      // 🎯 PRIORITÉ 6: Chercher dans les participants
      if (!phoneNumber && meetingDetails?.participants) {
        for (let i = 0; i < meetingDetails.participants.length; i++) {
          const participant = meetingDetails.participants[i];
          phoneNumber = participant.phoneNumber || participant.pstnNumber || participant.phone;
          if (phoneNumber) {
            console.log(`   ✅ Found phone in participant[${i}]:`, phoneNumber);
            break;
          }
        }
      }

      // 🎯 PRIORITÉ 7: Fallback - essayer depuis le displayName si c'est un numéro
      if (!phoneNumber && meetingDetails?.organizer?.displayName?.match(/^\+?[\d\s\-()]+$/)) {
        phoneNumber = meetingDetails.organizer.displayName;
        console.log('   ⚠️ Fallback: using displayName as phone:', phoneNumber);
      }

      // Nettoyer le numéro (enlever espaces, tirets, parenthèses)
      const cleanPhoneNumber = phoneNumber ? phoneNumber.replace(/[\s\-()]/g, '') : 'demo-phone';

      // ID de l'appelant (peut être différent du téléphone)
      const callerId = meetingDetails?.organizer?.id || meetingDetails?.participants?.[0]?.id || 'demo-user';

      console.log('📞 Final extracted values:');
      console.log('   📱 Phone Number (raw):', phoneNumber || '❌ NOT FOUND');
      console.log('   📱 Phone Number (clean):', cleanPhoneNumber);
      console.log('   👤 Caller ID:', callerId);
      console.log('   Using contextURL:', contextURL);
      console.log('   URL (original):', contextURL.url);
      console.log('   Method:', contextURL.method || 'GET');

      // Préparer les headers depuis contextURL
      let headers = {
        'Content-Type': 'application/json',
        ...contextURL.headers  // Merge headers from contextURL
      };

      // 🔄 Remplacer les placeholders dans les headers aussi (ex: X-Phone-Number: $phone)
      Object.keys(headers).forEach(key => {
        if (typeof headers[key] === 'string') {
          if (headers[key].includes('$phone') || headers[key].includes('{phone}')) {
            headers[key] = headers[key].replace(/\$phone|\{phone\}/g, cleanPhoneNumber);
            console.log(`   ✅ $phone replaced in header "${key}":`, headers[key]);
          }
          if (headers[key].includes('$callerId') || headers[key].includes('{callerId}')) {
            headers[key] = headers[key].replace(/\$callerId|\{callerId\}/g, callerId);
            console.log(`   ✅ $callerId replaced in header "${key}":`, headers[key]);
          }
        }
      });

      // 🔄 Remplacer les placeholders dans l'URL (ex: $phone, {phone}, $callerId)
      let apiUrl = contextURL.url;
      if (apiUrl.includes('$phone') || apiUrl.includes('{phone}')) {
        apiUrl = apiUrl.replace(/\$phone|\{phone\}/g, cleanPhoneNumber);
        console.log('   ✅ $phone replaced in URL!');
      }
      if (apiUrl.includes('$callerId') || apiUrl.includes('{callerId}')) {
        apiUrl = apiUrl.replace(/\$callerId|\{callerId\}/g, callerId);
        console.log('   ✅ $callerId replaced in URL!');
      }
      console.log('   🌐 Final API URL:', apiUrl);

      // Construire les options de la requête
      const requestOptions = {
        method: contextURL.method || 'GET',
        url: apiUrl,  // ✅ URL avec placeholders remplacés
        headers: headers,
        timeout: 15000 // 15 secondes timeout
      };

      // Ajouter les paramètres selon la méthode
      if (requestOptions.method.toUpperCase() === 'GET') {
        requestOptions.params = {
          phone: cleanPhoneNumber,       // 📱 Numéro de téléphone de l'appelant
          callerId: callerId,            // 👤 ID de l'appelant
          includeTickets: true,
          includeCallHistory: true
        };
      } else if (requestOptions.method.toUpperCase() === 'POST') {
        requestOptions.data = {
          phone: cleanPhoneNumber,       // 📱 Numéro de téléphone de l'appelant
          callerId: callerId,            // 👤 ID de l'appelant
          includeTickets: true,
          includeCallHistory: true
        };
      }

      console.log('   Request options:', requestOptions);

      // Faire la requête pour récupérer les données CRM
      const response = await axios(requestOptions);

      // Mettre à jour les données
      setCallerData(response.data);
      setIsLoading(false);

      console.log('✅ CRM data loaded successfully:', response.data);

    } catch (error) {
      console.error('❌ Error loading CRM data:', error);
      console.error('   Error details:', error.response?.data || error.message);

      // En cas d'erreur, charger des données de démonstration
      console.log('Falling back to demo data due to error');
      showNoClientData();
    }
  };

  // Aucune donnée client à afficher (CRM sans correspondance, pas de callContext, ou erreur).
  // Plus de données démo : CRMCallerInfo affichera le message traduit "pas de client".
  const showNoClientData = () => {
    setCallerData(null);
    setIsLoading(false);
  };

  // Afficher le skeleton ou les données
  return (
    <div className="App" data-theme={theme}>
      {!isLoading && (
        <nav className="app-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'crm'}
            className={`app-tab${activeTab === 'crm' ? ' app-tab-active' : ''}`}
            onClick={() => setActiveTab('crm')}
          >
            {t(locale, 'tab_customer')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'timeline'}
            className={`app-tab${activeTab === 'timeline' ? ' app-tab-active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            {t(locale, 'tab_timeline')}
          </button>
        </nav>
      )}
      <Suspense fallback={<SkeletonLoader />}>
        {isLoading ? (
          <SkeletonLoader />
        ) : activeTab === 'crm' ? (
          <CRMCallerInfo
            callerData={callerData}
            theme={theme}
            config={config}
            teamsContext={teamsContext}
            locale={locale}
          />
        ) : (
          <CallTimeline
            callState={callStateData}
            locale={locale}
          />
        )}
      </Suspense>
    </div>
  );
}

export default App;
